import { Alert, Button, Card, Empty, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ReportRun, ReportRunState } from '@goelectric/shared-types';
import {
  brandColors,
  darkColors,
  semanticColors,
  type StatusIconKey,
} from '@goelectric/ui-components';
import { StatusBadgeShell, StatusIcon, useStatusColor } from '@/components/StatusBadgeShell';
import { formatDateTime } from '../utils/format';
import { formatDestinatarios } from '../utils/email';

interface RunStateMeta {
  label: string;
  description: string;
  color: string;
  colorDark: string;
  iconKey: StatusIconKey;
}

/**
 * Metadatos de estado de una ejecución. Mismo criterio que el resto del design
 * system: color + icono + texto, nunca sólo color (WCAG).
 */
const RUN_STATE_META: Record<ReportRunState, RunStateMeta> = {
  PENDIENTE: {
    label: 'Pendiente',
    description: 'La ejecución está en cola.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'info',
  },
  GENERANDO: {
    label: 'Generando',
    description: 'El servidor está produciendo el archivo.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'bolt',
  },
  LISTO: {
    label: 'Listo',
    description: 'El archivo está disponible para descargar.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  ENVIADO: {
    label: 'Enviado',
    description: 'La ejecución ya fue entregada.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  ERROR: {
    label: 'Error',
    description: 'La generación falló.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
};

/** Estado de entrega por correo que reporta el proveedor (no el de generación). */
const ENTREGA_ESTADO_META: Record<
  NonNullable<ReportRun['entregaEstado']>,
  RunStateMeta
> = {
  PENDIENTE: {
    label: 'Entrega pendiente',
    description: 'El proveedor todavía no confirmó la entrega.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'info',
  },
  ENVIANDO: {
    label: 'Enviando',
    description: 'El correo se está enviando al proveedor.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'bolt',
  },
  ENTREGADO: {
    label: 'Entregado',
    description: 'El proveedor confirmó la entrega del correo.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  REBOTADO: {
    label: 'Rebotado',
    description: 'El correo no pudo entregarse (rebote).',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
  QUEJA: {
    label: 'Queja',
    description: 'El destinatario marcó el correo como spam.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'warning',
  },
  ERROR: {
    label: 'Error de envío',
    description: 'El envío falló tras los reintentos; el archivo sigue descargable.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
};

const FORMAT_LABELS: Record<ReportRun['formato'], string> = {
  json: 'Vista previa',
  csv: 'CSV',
  xlsx: 'Excel',
  pdf: 'PDF',
};

function RunStateBadge({ estado }: { estado: ReportRunState }) {
  const meta = RUN_STATE_META[estado];
  const color = useStatusColor(meta.color, meta.colorDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${meta.description}`}
    />
  );
}

function DeliveryStateBadge({
  estado,
  errorEnvio,
}: {
  estado: NonNullable<ReportRun['entregaEstado']>;
  errorEnvio?: string | null;
}) {
  const meta = ENTREGA_ESTADO_META[estado];
  const color = useStatusColor(meta.color, meta.colorDark);
  return (
    <Space direction="vertical" size={0}>
      <StatusBadgeShell
        color={color}
        icon={<StatusIcon iconKey={meta.iconKey} />}
        label={meta.label}
        title={errorEnvio ? `${meta.label}: ${errorEnvio}` : `${meta.label}: ${meta.description}`}
      />
      {errorEnvio && (
        <Typography.Text type="danger" style={{ fontSize: 12 }}>
          {errorEnvio}
        </Typography.Text>
      )}
    </Space>
  );
}

export interface ReportRunsPanelProps {
  runs: ReportRun[];
  /** Título visible por `reporteId` (la ejecución sólo guarda el id). */
  reportTitles: Record<string, string>;
  total: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  /** Error al cargar el historial. */
  error: string | null;
  /** Error del polling: corta las actualizaciones automáticas. */
  pollError: string | null;
  downloadingId: string | null;
  onRefresh: () => void;
  onLoadMore: () => void;
  onDownload: (run: ReportRun) => void;
  onRegenerate: (run: ReportRun) => void;
  /** Reenvía por correo: crea una ejecución nueva con los datos del run. */
  onResend: (run: ReportRun) => void;
}

/**
 * Descargas recientes: ejecuciones del tenant con su estado, total y acciones.
 * El polling vive en `useReportRuns`; este componente sólo pinta lo que recibe.
 */
export function ReportRunsPanel({
  runs,
  reportTitles,
  total,
  hasMore,
  loading,
  loadingMore,
  error,
  pollError,
  downloadingId,
  onRefresh,
  onLoadMore,
  onDownload,
  onRegenerate,
  onResend,
}: ReportRunsPanelProps) {
  const columns: ColumnsType<ReportRun> = [
    {
      key: 'reporte',
      title: 'Reporte',
      dataIndex: 'reporteId',
      ellipsis: true,
      render: (reporteId: string) => reportTitles[reporteId] ?? reporteId,
    },
    {
      key: 'fecha',
      title: 'Fecha',
      dataIndex: 'createdAt',
      render: (createdAt: string) => formatDateTime(createdAt),
    },
    {
      key: 'formato',
      title: 'Formato',
      dataIndex: 'formato',
      render: (formato: ReportRun['formato']) => FORMAT_LABELS[formato] ?? formato,
    },
    {
      key: 'entrega',
      title: 'Entrega',
      dataIndex: 'entrega',
      render: (_entrega: ReportRun['entrega'], run) => {
        if (run.entrega !== 'correo') {
          return (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Descarga
            </Typography.Text>
          );
        }
        return (
          <Space direction="vertical" size={2}>
            <Space size={4} wrap>
              <Tag color="blue" icon={<MailOutlined />} title={run.asunto ?? undefined}>
                Correo
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {formatDestinatarios(run.destinatarios)}
              </Typography.Text>
            </Space>
            {run.entregaEstado && (
              <DeliveryStateBadge estado={run.entregaEstado} errorEnvio={run.errorEnvio} />
            )}
          </Space>
        );
      },
    },
    {
      key: 'estado',
      title: 'Estado',
      dataIndex: 'estado',
      render: (estado: ReportRunState, run) => (
        <Space direction="vertical" size={0}>
          <RunStateBadge estado={estado} />
          {estado === 'ERROR' && run.error && (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {run.error}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      key: 'total',
      title: 'Filas',
      dataIndex: 'total',
      align: 'right',
      render: (filas: number | null | undefined, run) => (
        <Space size={4}>
          <span>{filas == null ? '—' : filas.toLocaleString('es-CO')}</span>
          {run.truncado && <Tag color="warning">Truncado</Tag>}
        </Space>
      ),
    },
    {
      key: 'acciones',
      title: '',
      width: 280,
      render: (_, run) => {
        const fileReady = run.estado === 'LISTO' || run.estado === 'ENVIADO';
        const puedeReenviar = run.entrega === 'correo';

        if (puedeReenviar) {
          return (
            <Space size={0} wrap>
              {fileReady && (
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={downloadingId === run.id}
                  onClick={() => onDownload(run)}
                >
                  Descargar archivo
                </Button>
              )}
              <Button
                type="link"
                size="small"
                icon={<MailOutlined />}
                onClick={() => onResend(run)}
              >
                Reenviar
              </Button>
            </Space>
          );
        }

        if (fileReady) {
          return (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              loading={downloadingId === run.id}
              onClick={() => onDownload(run)}
            >
              Descargar archivo
            </Button>
          );
        }
        if (run.estado === 'ERROR') {
          return (
            <Button type="link" size="small" icon={<ReloadOutlined />} onClick={() => onRegenerate(run)}>
              Generar de nuevo
            </Button>
          );
        }
        return (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            En proceso…
          </Typography.Text>
        );
      },
    },
  ];

  return (
    <Card
      className="reportes-runs"
      title="Descargas recientes"
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={onRefresh} loading={loading}>
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudieron cargar las descargas recientes"
          description={error}
          action={
            <Button size="small" onClick={onRefresh}>
              Reintentar
            </Button>
          }
        />
      )}

      {pollError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Se detuvo la actualización automática"
          description={pollError}
          action={
            <Button size="small" onClick={onRefresh}>
              Actualizar
            </Button>
          }
        />
      )}

      <Table<ReportRun>
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={runs}
        pagination={false}
        scroll={{ x: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Todavía no hay descargas recientes"
            />
          ),
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
        <Typography.Text type="secondary">
          {runs.length} de {total}
        </Typography.Text>
        {hasMore && (
          <Button size="small" loading={loadingMore} onClick={onLoadMore}>
            Ver más
          </Button>
        )}
      </div>
    </Card>
  );
}
